/**
 * **„Inaczej niż zwykle"** — every date that is not its weekday and every block still ahead, as the
 * Grafik tab's right card lists them and marks them on its month (T-B2).
 *
 * Two kinds, one list, in the order they happen: an EXCEPTION (a date override, R3 — other hours on
 * that date, or closed), which „Przywróć wzorzec" deletes so the date follows its weekday again; and
 * a BLOCK (R4 — an absolute stretch), which „Usuń" deletes. What has already happened is not listed:
 * an exception on a past date and a block that has ended change nothing a client could still book.
 *
 * Everything is read on the trainer's clock: an override's date is a civil date there, and a
 * block's two instants are read into it.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailabilityResponse } from '~/shared/api/generated/model';

import { SCHEDULE_READ_AHEAD_DAYS, usualHours } from './dateException';
import { mondayOfDay, shiftDay, wallReading, zonedMidnight, type CivilDay } from './trainerClock';
import {
  blockSpanOn,
  trainerBlocks,
  workingDays,
  type TrainerBlock,
  type WorkingWindow,
} from './workingHours';

export type Deviation =
  | {
      readonly kind: 'exception';
      readonly date: CivilDay;
      /** Empty = the date is closed. */
      readonly windows: readonly WorkingWindow[];
      /** The weekday's own hours that date — what the exception replaces. Empty = usually free. */
      readonly usual: readonly WorkingWindow[];
    }
  | {
      readonly kind: 'block';
      readonly block: TrainerBlock;
      readonly fromDay: CivilDay;
      readonly fromMinutes: number;
      readonly toDay: CivilDay;
      readonly toMinutes: number;
    };

/** A day of the card's month: what, if anything, is unusual about it. */
export interface DeviationMark {
  /** The date's exception: its hours, or closed. `null` = the date follows its weekday. */
  readonly exception: { readonly windows: readonly WorkingWindow[] } | null;
  /** At least one block touches the date. */
  readonly blocked: boolean;
}

/** Every exception from today on and every block not yet over, soonest first. */
export function upcomingDeviations(
  availability: TrainerAvailabilityResponse,
  context: { readonly today: CivilDay; readonly nowMs: number; readonly zone: string },
): Deviation[] {
  const exceptions = (availability.overrides ?? []).flatMap((entry) => {
    const date = entry.date?.slice(0, 10);
    if (date === undefined || date < context.today) return [];
    const [working] = workingDays(availability, [date]);
    return [
      {
        kind: 'exception' as const,
        date,
        windows: working?.windows ?? [],
        usual: usualHours(availability, date),
        sortMs: zonedMidnight(date, context.zone),
      },
    ];
  });
  const blocks = trainerBlocks(availability)
    .filter((block) => block.endMs > context.nowMs)
    .map((block) => {
      const from = wallReading(block.startMs, context.zone);
      const to = wallReading(block.endMs, context.zone);
      return {
        kind: 'block' as const,
        block,
        fromDay: from.day,
        fromMinutes: from.minutes,
        toDay: to.day,
        toMinutes: to.minutes,
        sortMs: block.startMs,
      };
    });
  return [...exceptions, ...blocks]
    .sort((a, b) => a.sortMs - b.sortMs)
    .map(({ sortMs: _sortMs, ...deviation }) => deviation);
}

/**
 * The card's month: five weeks from the Monday of today's week, so today is always on the first row
 * and what is coming fills the rest.
 */
export function deviationMonth(today: CivilDay): CivilDay[] {
  const monday = mondayOfDay(today);
  return Array.from({ length: 35 }, (_, index) => shiftDay(monday, index));
}

/** What each of `days` carries. A day with nothing unusual maps to an empty mark. */
export function deviationMarks(
  availability: TrainerAvailabilityResponse,
  days: readonly CivilDay[],
  zone: string,
): Map<CivilDay, DeviationMark> {
  const blocks = trainerBlocks(availability);
  const marks = new Map<CivilDay, DeviationMark>();
  for (const working of workingDays(availability, days)) {
    marks.set(working.day, {
      exception: working.source === 'override' ? { windows: working.windows } : null,
      blocked: blocks.some((block) => blockSpanOn(block, working.day, zone) !== null),
    });
  }
  return marks;
}

/**
 * The dates the Grafik tab reads its exceptions and blocks over: from yesterday, 180 days in all —
 * the most one availability read may cover (`BookingDefaults.MaxRangeDays`, R3). Yesterday, because
 * a block that began then may still be running. The weekly grid is not ranged; it comes whole.
 */
export function scheduleReadRange(today: CivilDay): {
  readonly first: CivilDay;
  readonly last: CivilDay;
} {
  return { first: shiftDay(today, -1), last: shiftDay(today, SCHEDULE_READ_AHEAD_DAYS) };
}

/**
 * The dates the client's-phone preview reads free starts over: today to the horizon, held to the
 * same 180-day read the server allows.
 */
export function previewReadRange(
  today: CivilDay,
  horizonDays: number,
): { readonly first: CivilDay; readonly last: CivilDay } {
  return { first: today, last: shiftDay(today, Math.min(Math.max(horizonDays, 1), 179)) };
}
